import { useRef, MouseEvent } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { MaskLines, Reveal, EASE } from "@/components/site/Motion";
import { scrollToId } from "@/components/site/SmoothScroll";
import { HERO_FEATURES, SITE, waLink } from "@/lib/content";
import { LeafCutout } from "@/components/kit/Decor";
import { Icon } from "@/components/kit/Icon";

function Badge() {
  return (
    <div className="relative h-24 w-24 sm:h-32 sm:w-32" data-testid="hero-rotating-badge" aria-hidden="true">
      <svg viewBox="0 0 120 120" className="h-full w-full animate-spin-slow">
        <defs><path id="circ" d="M60 60m-46 0a46 46 0 1 1 92 0a46 46 0 1 1-92 0" /></defs>
        <circle cx="60" cy="60" r="59" fill="#dcae4f" />
        <text fontSize="9" fontWeight="700" fill="#1c3320" fontFamily="Hanken Grotesk, sans-serif">
          <textPath href="#circ" textLength="286" lengthAdjust="spacing">FREE 20-MINUTE CONSULTATION · NO OBLIGATION · </textPath>
        </text>
      </svg>
      <span className="absolute inset-0 grid place-items-center font-display text-3xl italic text-forest-deep">20′</span>
    </div>
  );
}

function Bowl() {
  const { scrollY } = useScroll();
  const rotate = useTransform(scrollY, [0, 900], [0, 50]);
  const y = useTransform(scrollY, [0, 900], [0, 90]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 100, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 100, damping: 16 });
  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[540px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <div className="pointer-events-none absolute inset-[6%] rounded-full bg-[radial-gradient(closest-side,rgba(226,196,126,0.38),rgba(226,196,126,0.06)_60%,transparent)]" />
      <motion.div className="absolute inset-[4%] rounded-full border border-gold/30" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.6, delay: 0.3, ease: EASE }} />
      <motion.div style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000, y }} className="absolute inset-[9%]">
        <motion.img
          src="/images/bowl.webp"
          alt="Top-down bowl of avocado, chickpeas, tomatoes, cabbage and fresh greens"
          style={{ rotate }}
          initial={{ scale: 0.5, opacity: 0, rotate: -40 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.6, delay: 0.25, ease: EASE }}
          className="h-full w-full object-contain drop-shadow-[0_50px_60px_rgba(0,0,0,0.5)]"
          fetchPriority="high"
          data-testid="hero-bowl-image"
        />
      </motion.div>
      <motion.div className="absolute -top-2 right-2 sm:right-6" initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 1.1, duration: 1, ease: EASE }}>
        <Badge />
      </motion.div>
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.3, duration: 0.8, ease: EASE }} className="absolute bottom-6 left-0 rounded-2xl bg-cream/95 px-5 py-3.5 shadow-xl backdrop-blur" data-testid="hero-founder-chip">
        <p className="text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-gold-ink">Sony's own journey</p>
        <p className="font-display text-4xl text-forest">{SITE.founderKg} kg</p>
      </motion.div>
    </div>
  );
}

function FeatureRow() {
  return (
    <div className="wrap relative z-10 mt-14 lg:mt-6" data-testid="hero-features">
      <div className="grid grid-cols-2 gap-y-8 border-t border-cream/10 pt-10 sm:grid-cols-3 lg:grid-cols-5 lg:divide-x lg:divide-cream/10">
        {HERO_FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.07} className={`flex items-center gap-4 lg:justify-center lg:px-4 ${i === 4 ? "col-span-2 sm:col-span-1" : ""}`}>
            <span className="icon-ring h-12 w-12"><Icon name={f.icon} className="h-5 w-5" /></span>
            <span>
              <span className="block text-sm font-semibold text-cream">{f.title}</span>
              <span className="block text-sm text-cream/75">{f.text}</span>
            </span>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function HomeHero() {
  const ref = useRef<HTMLElement>(null);
  return (
    <section ref={ref} data-testid="hero-section" className="grain relative overflow-hidden bg-forest-deep pb-16 pt-8 text-cream sm:pt-14">
      <LeafCutout variant="a" className="-right-16 -top-8 z-0 h-44 w-36 opacity-60 sm:-top-16 sm:h-[460px] sm:w-[380px] sm:opacity-100" speed={0.25} />
      <LeafCutout variant="b" className="-left-32 bottom-6 z-0 hidden h-44 w-72 opacity-70 lg:block" speed={0.1} flip />
      <div className="wrap relative z-10 grid items-center gap-10 lg:grid-cols-12">
        <div className="relative z-10 lg:col-span-6">
          <motion.p className="eyebrow-dark" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
            <span className="h-px w-8 bg-gold" /> Wellness coaching with {SITE.founder}
          </motion.p>
          <h1 className="mt-6 text-[4.2rem] leading-[0.9] sm:text-8xl lg:text-[8rem]" data-testid="hero-heading">
            <MaskLines onLoad delay={0.15} lines={["Nourish.", <em key="e" className="text-gold-soft">Energise.</em>, "Transform."]} />
          </h1>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.9, ease: EASE }}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-cream/85 sm:text-lg" data-testid="hero-subtext">
              Simple, sustainable wellness coaching to help you feel better, live better and stay consistent. {SITE.coachLine}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <button onClick={() => scrollToId("book")} data-testid="hero-book-button" className="btn-gold group !py-4 text-base">
                Book Free Consultation <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
              </button>
              <a href={waLink()} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button" className="btn-cream !py-4 text-base">
                <MessageCircle className="h-4 w-4" /> WhatsApp Sony
              </a>
            </div>
            <p className="mt-5 text-sm font-medium text-cream/85" data-testid="hero-trust-note">Free 20-minute consultation. No obligation.</p>
          </motion.div>
        </div>
        <div className="relative lg:col-span-6"><Bowl /></div>
      </div>
      <FeatureRow />
    </section>
  );
}
